/* eslint-disable react/button-has-type */
import React, { useState, useEffect, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './LotDetails.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTriangleExclamation, faCircleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '@mui/material/Tooltip';
import ItemCard from '../ItemCard/ItemCard';
import { LOT_STATUS } from '../../constants';
import { shippingApi } from '../../../../api/shipping';
import { updateReviewStatus } from '../../../../modules/shippingReview/thunks';

const LotDetails = forwardRef(({ lotInfo, openEditShippingModal, onConfirm }, ref) => {
  const dispatch = useDispatch();
  const shippingInfo = lotInfo?.shippingReviewData;

  const shipmentObjects = shippingInfo?.arguments?.objects || [];
  const objectCount = shipmentObjects.length || 0;
  const shippingLocation = shippingInfo?.arguments?.origin?.name || 'Unknown Location';
  const [showEditButton, setShowEditButton] = useState(true);
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  const [lowConfLabel, setLowConfLabel] = useState(false);
  const [mediumConfLabel, setMediumConfLabel] = useState(false);
  const [lotStatus, setLotStatus] = useState();
  const artaSubtypeMap = useSelector((state) => state.shippingReview.artaSubtypeMap);

  // Polling logic starts
  const pollInterval = 30000; // 30000 = 30 seconds
  const stopPolling = () => {
    if (!ref.current) return;
    if (ref.current?.[lotInfo.lotID]) {
      clearInterval(ref.current[lotInfo.lotID]);
      ref.current[lotInfo.lotID] = null;
    }
  };
  const asyncPoll = async () => {
    try {
      const data = await shippingApi.fetchReviewById(lotInfo.shippingReviewData.id, lotInfo.houseID);
      if (!data || !data.data || !data.data.review || !lotInfo) {
        stopPolling();
        throw new Error('Error in polling review data, please refresh the page');
      }
      dispatch(updateReviewStatus(data.data?.review));
    } catch (err) {
      console.error('Error fetching review while polling:', err);
      stopPolling();
    }
  };
  const startPolling = () => {
    ref.current = { ...ref.current, [lotInfo.lotID]: setInterval(asyncPoll, pollInterval) };
  };
  useEffect(() => {
    if (!lotInfo?.shippingReviewData) return;
    const status = lotInfo.shippingReviewData.status;
    const confidenceAllScore = lotInfo.shippingReviewData.objectsExtraction?.confidenceAll;
    const isManuallyModified = lotInfo?.shippingReviewData?.isLotModified;
    setLotStatus(status);
    if (status === 'completed') {
      setShowEditButton(false);
      setLowConfLabel(false);
      setMediumConfLabel(false);
      setShowConfirmButton(false);
    }
    if (status === 'pending') {
      setShowConfirmButton(false);
      setLowConfLabel(false);
      setMediumConfLabel(false);
    }
    if (status === 'awaiting_approval' && (confidenceAllScore >= 0.8 || isManuallyModified)) {
      setShowConfirmButton(true);
    }
    if ((status === 'failed' || (status === 'awaiting_approval' && confidenceAllScore < 0.5 && !isManuallyModified))) {
      setLowConfLabel(true);
    }
    if ((status === 'awaiting_approval' && confidenceAllScore >= 0.5 && confidenceAllScore < 0.8 && !isManuallyModified)) {
      setMediumConfLabel(true);
      setShowConfirmButton(true);
    }
  }, [lotInfo]);

  useEffect(() => {
    if (lotStatus === 'pending') {
      startPolling();
    } else {
      stopPolling();
    }
  }, [lotStatus]);
  // Polling logic ends

  const truncateTrailingZeros = (value) => parseFloat(value);

  const onClickEdit = () => {
    openEditShippingModal(lotInfo);
  };

  const onConfirmLot = () => {
    const payload = { review: { reviewComplete: true } };
    onConfirm(lotInfo.lotID, shippingInfo.id, lotInfo.houseID, payload);
  };

  const getItemCard = ({ id, item, confLevels, isSingle, isManuallyModified }) => {
    const dimensionsArr = ['depth', 'height', 'width'];
    const nums = dimensionsArr.map(el => confLevels?.[el])?.filter(num => num !== undefined);
    const lowestConfLevel = nums.length > 0 ? Math.min(...nums) : 0;

    return (<div className="lot-table" key={id}>
      <ItemCard
        dimensions={
          {
            value: `${truncateTrailingZeros(item.height)} x ${truncateTrailingZeros(item.width)} x ${truncateTrailingZeros(item.depth)} ${item.unitOfMeasurement}`,
            conf: lowestConfLevel
          }
        }
        type={{ value: artaSubtypeMap?.[item.artaSubtype]?.name, conf: confLevels?.artaSubtype }}
        fragile={{ value: item.isFragile, conf: confLevels?.isFragile }}
        name={{ value: item.title }}
        fulfillmentLocation={shippingLocation}
        single={isSingle}
        lotStatus={lotStatus}
        isManuallyModified={isManuallyModified}
      />
    </div>
  )};

  return (
    <div className="lot-details" data-shipping-review-id={lotInfo.shippingReviewID}>
      <div className="lot-thumbnail">
        <img src={lotInfo.imageUrl} alt="Thumbnail" />
      </div>
      <div className="lot-info">
        <div className="lot-header">
          <strong>#{lotInfo.lotNumber}{lotInfo.lotExt} {lotInfo.lotTitle}</strong>
          {objectCount > 1 && <p>This lot has {objectCount} items shipping from {shippingLocation}</p>}
        </div>
        {objectCount > 1 && (
          shipmentObjects && shipmentObjects.map((item) => {
            return (
            <div className="lot-table" key={item.id}>
              {getItemCard({
                item,
                id: item.id,
                confLevels: shippingInfo?.confidenceLevels?.[item.id],
                isSingle: false,
                isManuallyModified:  shippingInfo?.isLotModified
              })}
            </div>
          )})
        )}
        {objectCount === 1 && (
          <div className="lot-table">
            {getItemCard({
              item: shipmentObjects[0],
              id: shipmentObjects[0].id,
              confLevels: shippingInfo?.confidenceLevels?.[shipmentObjects[0]?.id],
              isSingle: true,
              isManuallyModified:  shippingInfo?.isLotModified
            })}
          </div>
        )}
      </div>
      <div className="lot-actions">
        {showEditButton && <button className="detail-edit-button" onClick={onClickEdit}>Edit</button>}
        {showConfirmButton && <button className="detail-confirm-button" onClick={onConfirmLot}>Confirm</button>}
        {lotStatus === 'completed' && <span className="success-text">{LOT_STATUS.completed} <FontAwesomeIcon icon={faCheckCircle} /></span>}
        {lowConfLabel && (
          <Tooltip title="This lot requires review in order to confirm shipping data. Please edit and review the data." placement="left">
            <span className="error-text">Action Required <FontAwesomeIcon icon={faTriangleExclamation} /></span>
          </Tooltip>
        )}
        {mediumConfLabel && (
          <Tooltip title="This lot has a suggested review before confirming accurate shipping data." placement="left">
            <span className="warning-text">Review Suggested <FontAwesomeIcon icon={faCircleExclamation} /></span>
          </Tooltip>
        )}
        {(lotStatus === 'pending' || lotStatus === 'processing') && <span className="warning-text">{LOT_STATUS.pending} <FontAwesomeIcon icon={faSpinner} /></span>}
      </div>
    </div>
  );
});

export default LotDetails;
